import React from 'react'
import { FolderStatus } from '@alucio/aws-beacon-amplify/src/models'
import { FolderORM } from 'src/types/types'
import {
  useDNAFolderActions,
  folderContextMenuActions,
  FolderContextMenuAction,
} from './DNAFolder.actions'
import { DNAContextMenu } from '@alucio/lux-ui'
import { useAppSettings } from 'src/state/context/AppSettings'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import { useCurrentUser } from 'src/state/redux/selector/user'
import { isSharedEditableFolder } from 'src/utils/foldersHelpers'

export interface DNAFolderContextMenuProps {
  folderORM: FolderORM,
  actions?: FolderContextMenuAction[],
  children: React.ReactElement,
}

export const DNADocumentContextMenu: React.FC<DNAFolderContextMenuProps> = (props) => {
  const {
    folderORM,
    actions,
    children,
  } = props
  const DNAFolderActions = useDNAFolderActions()
  const { isOnline } = useAppSettings()
  const { isSharedWithTheUser } = folderORM.meta
  const currentUser = useCurrentUser()
  const { status } = folderORM.model
  const isFavorite = folderORM.meta.isFavorite
  const { parentFolderORM } = folderORM.relations
  const isActive = status === FolderStatus.ACTIVE
  const isArchived = status === FolderStatus.ARCHIVED || parentFolderORM?.model.status === FolderStatus.ARCHIVED
  const isNestedFolder = !!parentFolderORM
  const enableSharedFolders = useFeatureFlags('enableSharedFolders');
  const isSharedEditFolder = isSharedEditableFolder(folderORM, currentUser.userProfile?.id)

  /** Start with no actions */
  const actionsToUse: FolderContextMenuAction[] = []

  /** Identify conditions for enabling each action */
  const canArchive = !isNestedFolder && !isSharedWithTheUser
  const canCopyShared = isSharedWithTheUser
  const canDelete = (!isSharedWithTheUser || (isSharedWithTheUser && isSharedEditFolder && isNestedFolder))
  const canDuplicate = !isArchived && !isNestedFolder && !isSharedWithTheUser
  const canDuplicateNested = !isArchived && isNestedFolder && !isSharedWithTheUser
  const canPin = !isArchived && !isNestedFolder
  const canRename = !isArchived && (!isSharedWithTheUser ||
    (isSharedWithTheUser && isSharedEditFolder && isNestedFolder))
  const canShare = isActive && !isNestedFolder && !isSharedWithTheUser && enableSharedFolders && isOnline;

  /** Populate actions array - note that this order affects the order
   * in which the elements are presented to the user in the UI */
  canPin && actionsToUse.push(isFavorite ? 'unpin' : 'pin')
  canDuplicate && actionsToUse.push('duplicate')
  canDuplicateNested && actionsToUse.push('duplicateNested')
  canArchive && actionsToUse.push(isArchived ? 'unarchive' : 'archive')
  canShare && actionsToUse.push('share')
  canCopyShared && actionsToUse.push('copyShared')
  canRename && actionsToUse.push('rename')
  canDelete && actionsToUse.push('delete')

  return (
    <DNAContextMenu>
      <DNAContextMenu.Anchor>
        {children}
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        {
          (actions ?? actionsToUse).map(action => {
            const { onPress, ...rest } = folderContextMenuActions[action]
            return (
              <DNAContextMenu.Item
                key={action}
                status="primary"
                onPress={DNAFolderActions[action](folderORM)}
                {...rest}
              />
            )
          })
        }
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

export default DNADocumentContextMenu
